(ns client-portal.client.user-management.user.data
  (:require [client-portal.client.user-management.data :as um.d]
            [clojure.set :as set]
            [clojure.string :as s]
            [shared.core.account.data :as account.d]
            [shared.core.reactive :as re]
            [shared.core.util :as util]))

(def form-path (conj um.d/root-path :user-form))
(def save-path (conj um.d/root-path :user-save))
(def invitation-request-path (conj um.d/root-path :invitation-request))

(def email-pattern #"(?i)[^@\s]+@[^@\s]+\.[^@\s]+")

(defn- editable [{:keys [active application-ids application-role-ids tenant-organization-ids]} location-limits]
  {:active (boolean active)
   :application-ids (set application-ids)
   :application-role-ids (set application-role-ids)
   :organizations (mapv (fn [id]
                          {:key (util/random-uuid-string)
                           :tenant-organization-id id})
                        tenant-organization-ids)
   :location-limits (vec location-limits)})

(re/reg-event-db
  :user-management/init-user-form re/middleware
  (fn [db [form-id user-id]]
    (let [user (when user-id
                 (um.d/tenant-data db :user user-id))
          organizations (um.d/rows (um.d/tenant-data db :organizations))
          preselected-organization-id (or (some #{(um.d/selected-organization-id db)} (map :tenant-organization-id organizations))
                                          (when (= 1 (count organizations))
                                            (-> organizations first :tenant-organization-id)))
          values (if user
                   (editable user (:location-limits user))
                   (editable {:active true
                              :tenant-organization-ids (some-> preselected-organization-id (vector))}
                             []))]
      (assoc-in db form-path (merge values
                                    {:form-id form-id
                                     :user-id user-id
                                     :email-address (or (:email-address user) "")
                                     :original values})))))

(re/reg-sub :user-management/user-form- #(get-in % form-path))

(re/reg-event-db
  :user-management/set-user-field re/middleware
  (fn [db [k v]]
    (assoc-in db (conj form-path k) v)))

(re/reg-event-db
  :user-management/add-user-organization re/middleware
  (fn [db [tenant-organization-id]]
    (update-in db (conj form-path :organizations) conj {:key (util/random-uuid-string)
                                                         :tenant-organization-id tenant-organization-id})))

(re/reg-event-db
  :user-management/remove-user-organization re/middleware
  (fn [db [row-key]]
    (update-in db (conj form-path :organizations) #(filterv (fn [row] (not= row-key (:key row))) %))))

(re/reg-event-db
  :user-management/set-user-organization-connections re/middleware
  (fn [db [row-key connections]]
    (update-in db (conj form-path :organizations) #(mapv (fn [row]
                                                           (cond-> row
                                                                   (= row-key (:key row)) (assoc :connections (vec connections))))
                                                         %))))

(defn- application-role-ids [db application-id]
  (->> (um.d/rows (get-in db um.d/roles-path))
       (filter #(= application-id (:application-id %)))
       (map :id)
       (set)))

(re/reg-event-db
  :user-management/set-application-access re/middleware
  (fn [db [application-id on?]]
    (if on?
      (update-in db (conj form-path :application-ids) conj application-id)
      (-> db
          (update-in (conj form-path :application-ids) disj application-id)
          (update-in (conj form-path :application-role-ids) set/difference (application-role-ids db application-id))))))

(re/reg-event-db
  :user-management/set-application-roles re/middleware
  (fn [db [application-id role-ids]]
    (cond-> (update-in db (conj form-path :application-role-ids) #(-> (set/difference % (application-role-ids db application-id))
                                                                      (into role-ids)))
            (not-empty role-ids) (update-in (conj form-path :application-ids) conj application-id))))

(defn limit-key [{:keys [kind cohort-id location-id]}]
  [kind cohort-id location-id])

(defn- same-limit? [a b]
  (= (limit-key a) (limit-key b)))

(re/reg-event-db
  :user-management/add-location-limit re/middleware
  (fn [db [limit]]
    (update-in db (conj form-path :location-limits) #(cond-> %
                                                             (not-any? (partial same-limit? limit) %) (conj limit)))))

(re/reg-event-db
  :user-management/remove-location-limit re/middleware
  (fn [db [limit]]
    (update-in db (conj form-path :location-limits) #(filterv (fn [l] (not (same-limit? limit l))) %))))

(re/reg-event-db
  :user-management/remove-concept-limits re/middleware
  (fn [db [concept-id]]
    (update-in db (conj form-path :location-limits) #(filterv (fn [l] (not= concept-id (:concept-id l))) %))))

(re/reg-event-db
  :user-management/discard-user-changes re/middleware
  (fn [db]
    (update-in db form-path #(merge % (:original %)))))

(defn default-connections
  "A new member signs in through SoundHound's own connection when they're internal, or the organization's only one"
  [available email-address]
  (let [names (map :name available)
        soundhound (when (util/soundhound-email? email-address)
                     (some #(when (s/includes? (s/lower-case %) "soundhound") %) names))]
    (cond
      soundhound [soundhound]
      (= 1 (count names)) (vec names)
      :else [])))

(defn row-connections
  "The sign-in methods a row will save: what was picked, else what Auth0 has for an existing membership, else a default"
  [db {:keys [user-id email-address original]} {:keys [tenant-organization-id connections]}]
  (let [existing? (some #(= tenant-organization-id (:tenant-organization-id %)) (:organizations original))]
    (cond
      connections connections
      existing? (some->> (um.d/rows (um.d/tenant-data db :user-connections user-id))
                         (some #(when (= tenant-organization-id (:tenant-organization-id %)) %))
                         :connections)
      :else (some-> (um.d/rows (um.d/tenant-data db :organization-connections tenant-organization-id))
                    (default-connections email-address)))))

(re/reg-sub
  :user-management/row-connections-
  (fn [db [_ row]]
    (row-connections db (get-in db form-path) row)))

(defn- comparable
  "Everything editable as one map, so each status flip, application, role, organization (or change to how they sign in
   to one) and location limit is a single key"
  [db form values]
  (merge {:active (:active values)}
         (zipmap (map #(vector :application %) (:application-ids values)) (repeat true))
         (zipmap (map #(vector :role %) (:application-role-ids values)) (repeat true))
         (into {} (map (fn [row]
                         [[:organization (:tenant-organization-id row)] (set (row-connections db form row))])
                       (:organizations values)))
         (zipmap (map #(into [:limit] (limit-key %)) (:location-limits values)) (repeat true))))

(defn unsaved-count [db]
  (let [{:keys [original] :as form} (get-in db form-path)
        now (comparable db form form)
        before (comparable db form original)]
    (->> (set/union (set (keys now)) (set (keys before)))
         (filter #(not= (get now %) (get before %)))
         (count))))

(re/reg-sub :user-management/user-unsaved-count- unsaved-count)

(defn internal-role-labels
  "The internal-only roles the form gives someone without a SoundHound address"
  [db]
  (let [{:keys [email-address application-role-ids]} (get-in db form-path)]
    (when (and (not (s/blank? email-address)) (not (util/soundhound-email? email-address)))
      (->> (um.d/rows (get-in db um.d/roles-path))
           (filter #(and (:internal %) (contains? application-role-ids (:id %))))
           (map :label)
           (sort)))))

(defn internal-roles-message [labels]
  (str (s/join ", " labels)
       (if (= 1 (count labels)) " is" " are")
       " internal only, so only @soundhound.com addresses can have "
       (if (= 1 (count labels)) "it" "them")
       ". Remove "
       (if (= 1 (count labels)) "it" "them")
       " to save."))

(defn problems
  "What has to be fixed before the form can save, keyed by where to show it"
  [db]
  (let [{:keys [user-id email-address organizations original]} (get-in db form-path)
        email-address (some-> email-address (s/trim) (s/lower-case))
        existing-user (when-not user-id
                        (some #(when (= email-address (some-> % :email-address (s/lower-case))) %)
                              (um.d/rows (um.d/tenant-data db :users))))
        internal-labels (internal-role-labels db)
        grantable-role-ids (um.d/grantable-role-ids (account.d/account-user-roles db) (get-in db um.d/roles-path))]
    (cond-> {}
            (not-empty internal-labels)
            (assoc :roles (internal-roles-message internal-labels))

            (and user-id grantable-role-ids (not-empty (set/difference (:application-role-ids original) grantable-role-ids)))
            (assoc :roles (str email-address " has roles you don't, so only an admin can change their access."))

            (and (nil? user-id) (s/blank? email-address))
            (assoc :email-address "Enter their email address")

            (and (nil? user-id) (not (s/blank? email-address)) (not (re-matches email-pattern email-address)))
            (assoc :email-address "Enter a valid email address, like name@company.com")

            existing-user
            (assoc :email-address {:message (str email-address " already has access here.")
                                   :user-id (:public-id existing-user)})

            (and (nil? user-id) (empty? organizations))
            (assoc :organizations "Add at least one organization")

            (some (fn [row]
                    (and (not (some #(= (:tenant-organization-id row) (:tenant-organization-id %))
                                    (get-in db (conj form-path :original :organizations))))
                         (empty? (row-connections db (get-in db form-path) row))))
                  organizations)
            (assoc :connections "Choose how they'll sign in to each new organization"))))

(re/reg-sub :user-management/user-problems- problems)

(defn- save-request [db]
  (let [{:keys [user-id email-address active application-ids application-role-ids organizations location-limits original]
         :as form} (get-in db form-path)
        original-organization-ids (set (map :tenant-organization-id (:organizations original)))
        organizations-by-id (um.d/by-id :tenant-organization-id (um.d/tenant-data db :organizations))
        ;; Limits on a concept none of their organizations cover anymore aren't shown, so they aren't kept either
        covered-concept-ids (set (mapcat #(get-in organizations-by-id [(:tenant-organization-id %) :concept-ids]) organizations))]
    {:method :post
     :url (um.d/with-tenant "/api/user-management/users" (um.d/current-tenant db))
     :json-params (util/->camelCaseKeywords
                    (cond-> {:email-address email-address
                             :active active
                             :application-ids (vec application-ids)
                             :application-role-ids (vec application-role-ids)
                             ;; Auth0 is only touched for organizations that are new, or whose sign-in methods changed
                             :organizations (mapv (fn [{:keys [tenant-organization-id connections] :as row}]
                                                    {:tenant-organization-id tenant-organization-id
                                                     :connections (if (or connections
                                                                          (not (contains? original-organization-ids tenant-organization-id)))
                                                                    (vec (row-connections db form row))
                                                                    [])})
                                                  organizations)
                             :location-limits (->> location-limits
                                                   (filter #(contains? covered-concept-ids (:concept-id %)))
                                                   (mapv #(select-keys % [:kind :concept-id :cohort-id :location-id])))}
                            user-id (assoc :public-id user-id)))}))

(re/auth0-http-request
  :user-management/save-user
  save-path
  (fn [db _]
    (when (empty? (problems db))
      (save-request db)))
  :on-success (fn [db [{:keys [body]}] _]
                (let [{:keys [user-id]} (get-in db form-path)
                      {:keys [public-id email-address tenant-organization-ids]} body
                      tenant (um.d/current-tenant db)]
                  (um.d/reload-lists tenant)
                  (cond
                    (empty? tenant-organization-ids)
                    (do
                      (re/dispatch [:toasts/add-info {:msg (str "Removed " email-address " from every organization here")
                                                      :duration 5000}])
                      (re/dispatch [:user-management/go :user-management {:section "users"}])
                      db)

                    (nil? user-id)
                    (do
                      (re/dispatch [:toasts/add-info {:msg (str "Added " email-address)
                                                      :duration 4000}])
                      (re/dispatch [:user-management/go :user-management/user {:user-id public-id}])
                      (update-in db (um.d/tenant-path tenant :user) dissoc public-id))

                    :else
                    (do
                      (re/dispatch [:toasts/add-info {:msg "Changes saved"
                                                      :duration 3000}])
                      (re/dispatch [:user-management/load-user public-id tenant :reload])
                      (re/dispatch [:user-management/load-user-connections public-id tenant :reload])
                      (update-in db form-path #(assoc % :original (select-keys % [:active :application-ids :application-role-ids
                                                                                  :organizations :location-limits])))))))
  :on-fail (fn [db {:keys [body]} _]
             (re/dispatch [:toasts/add-error {:msg (or (:message body) "Couldn't save. Try again.")
                                              :duration 6000}])
             db))

(re/reg-sub :user-management/saving-user?- #(get-in % (conj save-path :request-active?)))

(re/auth0-http-request
  :user-management/resend-invitation
  invitation-request-path
  (fn [db {:keys [user-id tenant-organization-id]}]
    {:method :post
     :url (um.d/with-tenant "/api/user-management/invitations/resend" (um.d/current-tenant db))
     :json-params {:userId user-id
                   :tenantOrganizationId tenant-organization-id}})
  :on-success (fn [db _ _]
                (re/dispatch [:toasts/add-info {:msg "Invitation resent"
                                                :duration 3000}])
                (re/dispatch [:user-management/load-invitations (um.d/current-tenant db) :reload])
                db)
  :on-fail (fn [db {:keys [body]} _]
             (re/dispatch [:toasts/add-error {:msg (or (:message body) "Couldn't resend the invitation. Try again.")
                                              :duration 5000}])
             db))

(re/auth0-http-request
  :user-management/revoke-invitation
  invitation-request-path
  (fn [db {:keys [user-id tenant-organization-id]}]
    {:method :post
     :url (um.d/with-tenant "/api/user-management/invitations/revoke" (um.d/current-tenant db))
     :json-params {:userId user-id
                   :tenantOrganizationId tenant-organization-id}})
  :on-success (fn [db _ _]
                (re/dispatch [:toasts/add-info {:msg "Invitation revoked"
                                                :duration 3000}])
                (re/dispatch [:user-management/load-invitations (um.d/current-tenant db) :reload])
                db)
  :on-fail (fn [db {:keys [body]} _]
             (re/dispatch [:toasts/add-error {:msg (or (:message body) "Couldn't revoke the invitation. Try again.")
                                              :duration 5000}])
             db))

(re/reg-sub :user-management/invitation-request-active?- #(get-in % (conj invitation-request-path :request-active?)))
